// Pure session-local edit + tax-seed helpers for the Review Purchase List
// screen. NO runtime model imports (TaxTypeModel is type-only) so this file
// is unit-testable without chain-loading report.ts → dateformat (ERR_REQUIRE_ESM).
import { CurrencyHelper } from '@/helpers/CurrencyHelper';
import type { TaxTypeModel } from '@/models/tax_type';

export interface ReviewLineEdit {
  supplierSku?: string;
  unitCost?: number;
  /** Tax as a fraction (0.15 = 15%). Presence means the user touched tax. */
  taxRate?: number;
  taxTypeId?: number;
  taxTypeName?: string;
}
export type ReviewLineEdits = Record<number, ReviewLineEdit>;

/** A supplier's default tax, seeded onto a vendor group's untouched lines. */
export interface SeededTax {
  taxRate?: number;
  taxTypeId?: number;
  taxTypeName?: string;
}

/**
 * Tax % typed by the user (whole percent) → a partial line edit. Detaches the
 * line to "Custom": clears the tax type id and names it "Custom" when the rate
 * is positive (undefined when 0) so it no longer inherits the supplier default.
 * Mirrors Atlas useReviewLineEdits.setTaxPercent / poDraft.onTaxRateInput.
 */
export function taxPercentToEdit(percent: number | undefined): Partial<ReviewLineEdit> {
  const rate = percent != null && Number.isFinite(percent) ? percent / 100 : 0;
  return { taxRate: rate, taxTypeId: undefined, taxTypeName: rate > 0 ? 'Custom' : undefined };
}

/**
 * Effective tax for a line: the user's edit wins (presence of `taxRate` means
 * touched), else the group's seeded supplier default, else {}.
 */
export function effectiveTax(
  edit: ReviewLineEdit | undefined,
  seed: SeededTax | undefined,
): SeededTax {
  if (edit && edit.taxRate !== undefined) {
    return { taxRate: edit.taxRate, taxTypeId: edit.taxTypeId, taxTypeName: edit.taxTypeName };
  }
  return seed ?? {};
}

/** Map a resolved TaxTypeModel → a SeededTax (fraction rate). null/undefined → null. */
export function taxTypeToSeed(t: TaxTypeModel | null | undefined): SeededTax | null {
  if (!t) { return null; }
  return {
    taxRate: Number(t.rate) || 0,
    taxTypeId: t.id ?? undefined,
    taxTypeName: t.name ?? undefined,
  };
}

/** Effective unit cost for a line (edit wins). */
export function effectiveUnitCost(item: { unitCost: number }, edit: ReviewLineEdit | undefined): number {
  return edit?.unitCost ?? Number(item.unitCost);
}

/** Effective per-line total incl. tax, rounded per currency. */
export function effectiveLineTotal(
  item: { quantity: number; unitCost: number },
  edit: ReviewLineEdit | undefined,
  seed: SeededTax | undefined,
  currencyCode: string,
): number {
  const qty = Number(item.quantity) || 0;
  const unit = effectiveUnitCost(item, edit);
  const base = qty * unit;
  const rate = effectiveTax(edit, seed).taxRate ?? 0;
  const tax = CurrencyHelper.roundForCurrency(base * rate, currencyCode);
  return base + tax;
}

/** Sum of effective line totals across a group's items. */
export function groupTotal(
  items: Array<{ id: number; quantity: number; unitCost: number }>,
  edits: ReviewLineEdits,
  seed: SeededTax | undefined,
  currencyCode: string,
): number {
  return items.reduce((s, i) => s + effectiveLineTotal(i, edits[i.id], seed, currencyCode), 0);
}

/**
 * Expected-delivery default for a vendor group: `orderDate` (YYYY-MM-DD) plus
 * the supplier's lead time in days. Returns '' when the lead time is missing,
 * 0, negative, or non-finite — the supplier record stores a missing lead time
 * as 0 (indistinguishable from a real 0), so we leave the field blank rather
 * than pre-filling a misleading same-day date. Native-Date math (UTC via
 * toISOString) mirrors PurchaseOrderCreate.vue for parity; `orderDate` is the
 * already-formatted string, keeping this function pure and testable. Lead time
 * is expected to be a whole number of days (supplier-record / OLAP semantics);
 * a fractional value < 1 floors to same-day via setDate.
 */
export function expectedDeliveryFromLeadTime(
  orderDate: string,
  leadTime: number | null | undefined,
): string {
  if (typeof leadTime !== 'number' || !Number.isFinite(leadTime) || leadTime <= 0) {
    return '';
  }
  const d = new Date(orderDate);
  if (Number.isNaN(d.getTime())) {
    return '';
  }
  d.setDate(d.getDate() + leadTime);
  return d.toISOString().slice(0, 10);
}
